"use client";

import SectionHeader from "@/components/SectionHeader";
import { trackClickEvent } from "@/lib/tracking";
import { usePathname } from "next/navigation";

const caseStudies = [
  {
    title: "Feature Testing Agent for a SaaS Platform",
    industry: "B2B SaaS",
    problem:
      "Manual regression for each feature release was slowing down the pipeline and still missing edge cases.",
    solution:
      "We deployed a feature-testing agent connected to logs and CI, auto-generating smoke tests and test charters for risky areas.",
    impact: [
      "30–40% reduction in manual regression time per release.",
      "Faster feedback on newly deployed features through log-based anomaly hints.",
      "Reusable agent patterns for other services in the same org."
    ]
  },
  {
    title: "Agri-Insights Agent for Smallholder Farmers",
    industry: "Agriculture",
    problem:
      "Small teams needed to support hundreds of farmers with irrigation and input decisions across regions.",
    solution:
      "A WhatsApp-style interface backed by an agent that pulls weather, soil, and advisory content, responding in local languages.",
    impact: [
      "Higher adoption of recommended irrigation schedules.",
      "More structured data on crop issues across geography.",
      "Foundation for yield prediction and risk scoring."
    ]
  },
  {
    title: "Credit Card Advisor Agent for a Financial Services Pilot",
    industry: "Financial Services",
    problem:
      "Customers struggled to map their spend profile to the right card, leading to confusion and low conversion.",
    solution:
      "An agentic advisor that collects a short profile (spend categories, travel patterns, fees tolerance) and ranks suitable cards.",
    impact: [
      "Clearer explainability for why a card is recommended.",
      "Better mapping of lifestyle patterns to product features.",
      "Pilot-ready architecture for future bank integrations."
    ]
  }
];

export default function CaseStudiesPage() {
  const path = usePathname();

  function handleCaseStudyClick(title: string) {
    trackClickEvent("case_study_expand", path || "/case-studies", title);
  }

  return (
    <section className="pb-16">
      <SectionHeader
        eyebrow="Case Studies"
        title="Realistic paths from idea to production agents"
        subtitle="These examples illustrate how Bacha AI thinks about scope, integration, and measurable impact."
      />

      <div className="space-y-6">
        {caseStudies.map(cs => (
          <article
            key={cs.title}
            className="gradient-border bg-slate-950/70 p-5 text-sm text-slate-200"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold">{cs.title}</h3>
              <button
                type="button"
                onClick={() => handleCaseStudyClick(cs.title)}
                className="rounded-full bg-slate-800 px-3 py-1 text-[11px] text-slate-100"
              >
                Mark as relevant
              </button>
              <span className="rounded-full bg-slate-800 px-3 py-1 text-[11px] text-slate-200">
                {cs.industry}
              </span>
            </div>
            <div className="mt-3 grid gap-3 text-xs text-slate-300 md:grid-cols-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Problem
                </div>
                <p className="mt-1">{cs.problem}</p>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Solution
                </div>
                <p className="mt-1">{cs.solution}</p>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Impact
                </div>
                <ul className="mt-1 list-disc space-y-1 pl-4">
                  {cs.impact.map(point => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
